import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { t, Trans } from "@lingui/macro";
import { Box, Checkbox, Container, Tooltip, tooltipClasses, Typography } from "@mui/material";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import {
  WfButton,
  WfHeader,
  WfIconContentDraggableRegular,
  WfMenuList,
  WfMenuListItem,
} from "@WahooFitness/redesignr";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { getWorkoutTypeNames } from "../WorkoutProfiles/AddWorkoutProfile/WorkoutTypeNames";
import WfActionGroup from "@WahooFitness/redesignr/dist/Actions/WfActionGroup/WfActionGroup.js";
import SortableItem from "../SortableItem";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import noop from "@/services/noop";
import { motion } from "motion/react";

const TOOLTIP_TIMEOUT_MS = 2000;
const TOOLTOP_POPPER_OFFSET = [0, -8];

const ProfileItem = forwardRef(function ProfileItem(
  {
    profile,
    checked,
    checkDisabled,
    action,
    dndListeners,
    dndAttributes,
    style,
    hidden = false,
  }: {
    profile: WahooWorkoutTypeFamily;
    checked: boolean;
    onCheckedChange?: (checked: boolean) => void;
    checkDisabled: boolean;
    action?: () => void;
    dndListeners?: ReturnType<typeof useSortable>["listeners"];
    dndAttributes?: ReturnType<typeof useSortable>["attributes"];
    style?: React.CSSProperties;
    hidden?: boolean;
  },
  ref
) {
  const workoutTypeNames = useMemo(() => getWorkoutTypeNames(), []);
  const displayString = workoutTypeNames[WahooWorkoutTypeFamily[profile]];

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const actionWithTooltip = useCallback(() => {
    if (checkDisabled) {
      setTooltipOpen(true);
      setTimeout(() => setTooltipOpen(false), TOOLTIP_TIMEOUT_MS);
    } else {
      action?.();
    }
  }, [checkDisabled, action]);

  return (
    <Box
      ref={ref}
      {...dndAttributes}
      {...dndListeners}
      style={style}
      sx={{ opacity: hidden ? 0 : 1 }}
    >
      <Tooltip
        title={t`At least one item should be selected.`}
        open={tooltipOpen}
        placement="top"
        slotProps={{
          popper: {
            modifiers: [{ name: "offset", options: { offset: TOOLTOP_POPPER_OFFSET } }],
            sx: {
              [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                { marginBottom: "0px" },
            },
          },
          tooltip: {
            sx: {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              px: 1,
              py: 0.5,
            },
          },
        }}
      >
        <WfMenuListItem
          key={profile}
          variant="action"
          id={profile.toString()}
          content={
            <Box display="flex" flexDirection="row" gap={1}>
              <Checkbox
                checked={checked}
                disabled={checkDisabled}
                size="medium"
                sx={{ height: 24, width: 24 }}
              />
              <Typography variant="prose-base-medium">{displayString}</Typography>
            </Box>
          }
          action={actionWithTooltip}
          primaryRightComponentOverride={<WfIconContentDraggableRegular color="disabled" />}
        />
      </Tooltip>
    </Box>
  );
});

const ProfileSelectDialog = ({
  selectedProfiles: initialSelectedProfiles,
  onSave,
  onCancel,
}: {
  selectedProfiles: WahooWorkoutTypeFamily[];
  onSave: (profiles: WahooWorkoutTypeFamily[]) => void;
  onCancel: () => void;
}) => {
  const [selectedProfiles, setSelectedProfiles] = useState({
    [WahooWorkoutTypeFamily.Cycling]: initialSelectedProfiles.includes(
      WahooWorkoutTypeFamily.Cycling
    ),
    [WahooWorkoutTypeFamily.Running]: initialSelectedProfiles.includes(
      WahooWorkoutTypeFamily.Running
    ),
  });

  const activeProfileCount = useMemo(
    () => Object.values(selectedProfiles).filter(Boolean).length,
    [selectedProfiles]
  );

  const checkIsDisabled = useCallback(
    (profile: ProfileOption) => {
      return selectedProfiles[profile] && activeProfileCount === 1;
    },
    [selectedProfiles, activeProfileCount]
  );

  type ProfileOption = keyof typeof selectedProfiles;

  const [profilesInOrder, setProfilesInOrder] = useState<ProfileOption[]>(
    [WahooWorkoutTypeFamily.Cycling, WahooWorkoutTypeFamily.Running].sort(
      (a, b) =>
        (initialSelectedProfiles.includes(a) ? initialSelectedProfiles.indexOf(a) : Infinity) -
        (initialSelectedProfiles.includes(b) ? initialSelectedProfiles.indexOf(b) : Infinity)
    ) as ProfileOption[]
  );

  const toggleProfile = useCallback(
    (profile: ProfileOption) => {
      setSelectedProfiles((prev) => ({
        ...prev,
        [profile]: !prev[profile],
      }));
    },
    [setSelectedProfiles]
  );

  const moveProfile = useCallback(
    (oldIndex: number, newIndex: number) => {
      setProfilesInOrder((prev) => arrayMove(prev, oldIndex, newIndex));
    },
    [setProfilesInOrder]
  );

  const { sensors, activeId, handleDragStart, handleDragEnd } = useDragAndDrop(
    profilesInOrder,
    (profile) => profile.toString(),
    moveProfile,
    noop
  );

  const selectedProfilesInOrder = useMemo(
    () => profilesInOrder.filter((profile) => selectedProfiles[profile]),
    [profilesInOrder, selectedProfiles]
  );

  const saveDisabled = useMemo(() => {
    return (
      selectedProfilesInOrder.length === initialSelectedProfiles.length &&
      selectedProfilesInOrder.every((profile, index) => profile === initialSelectedProfiles[index])
    );
  }, [selectedProfilesInOrder, initialSelectedProfiles]);

  const handleSave = useCallback(() => {
    onSave(selectedProfilesInOrder);
  }, [onSave, selectedProfilesInOrder]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
      width="100%"
    >
      <Container maxWidth="sm" disableGutters>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <WfHeader
            hierarchy="H2"
            title={t`My profiles`}
            description={t`Selected profiles will be displayed on your Athlete Profile page. Drag and drop
              selected items to set the order of your profiles.`}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext items={profilesInOrder} strategy={verticalListSortingStrategy}>
              <WfMenuList variant="outlined">
                {profilesInOrder.map((profile) => (
                  <SortableItem key={profile} id={profile.toString()}>
                    <ProfileItem
                      key={profile}
                      profile={profile}
                      checked={selectedProfiles[profile]}
                      checkDisabled={checkIsDisabled(profile)}
                      action={() => !checkIsDisabled(profile) && toggleProfile(profile)}
                    />
                  </SortableItem>
                ))}
              </WfMenuList>
            </SortableContext>
            <DragOverlay>
              {activeId !== null && (
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{
                    scale: 1.05,
                    boxShadow:
                      "0 3px 14px 2px rgba(0, 0, 0, 0.08), 0 8px 10px 1px rgba(0, 0, 0, 0.08), 0 5px 5px -3px rgba(0, 0, 0, 0.12)",
                  }}
                  transition={{ type: "spring", duration: 0.2 }}
                >
                  <Box
                    border={1}
                    borderColor="divider"
                    bgcolor="background.level1"
                    borderRadius={2}
                    overflow="hidden"
                  >
                    <ProfileItem
                      profile={+activeId}
                      checked={selectedProfiles[+activeId as ProfileOption]}
                      checkDisabled={checkIsDisabled(+activeId as ProfileOption)}
                    />
                  </Box>
                </motion.div>
              )}
            </DragOverlay>
          </DndContext>
        </Box>
      </Container>
      <Container maxWidth="sm" disableGutters>
        <WfActionGroup orientation="vertical" fullWidth>
          <WfButton
            hierarchy="primary"
            size="large"
            disabled={saveDisabled}
            onClick={handleSave}
            fullWidth
          >
            <Trans>Save</Trans>
          </WfButton>
          <WfButton hierarchy="tertiary" size="large" onClick={onCancel} fullWidth>
            <Trans>Cancel</Trans>
          </WfButton>
        </WfActionGroup>
      </Container>
    </Box>
  );
};

export default ProfileSelectDialog;
